import { useEffect, useState } from "react";

import * as Sentry from "@sentry/browser";
import {
  V2Button,
  V2CopyButton,
  V2Dialog,
  V2DialogContent,
  V2DialogFooter,
  V2DialogHeader,
  V2Divider,
  V2Switch,
} from "@stacklet/ui";
import { graphql, useFragment, useMutation } from "react-relay";

import { useAlertContext } from "app/contexts";
import { SetAwsAccessRolesMutation } from "app/mutations/SetAwsAccessRoles";

import { type AccessModelSettings_awsAccessRoles$key } from "./__generated__/AccessModelSettings_awsAccessRoles.graphql";
import { GatewayRolesForm, setAwsAccessRolesUpdater } from "./GatewayRolesForm";

import type { SetAwsAccessRolesMutation as SetAwsAccessRolesMutationType } from "app/mutations/__generated__/SetAwsAccessRolesMutation.graphql";

function getExecutionRole(samplePrimaryTrust: string) {
  let executionRole = "";
  try {
    const awsPrincipal =
      JSON.parse(samplePrimaryTrust).Statement[0]?.Principal.AWS;
    executionRole =
      awsPrincipal?.find((arn: string) => arn.includes("execution")) || "";
  } catch (e) {
    Sentry.captureException(e);
  }

  return executionRole;
}

interface Props {
  queryRef: AccessModelSettings_awsAccessRoles$key;
}

export function AccessModelSettings({ queryRef }: Props) {
  const { alertDispatch } = useAlertContext();

  const [updateAwsAccessRoles, isInFlight] =
    useMutation<SetAwsAccessRolesMutationType>(SetAwsAccessRolesMutation);

  const data = useFragment<AccessModelSettings_awsAccessRoles$key>(
    graphql`
      fragment AccessModelSettings_awsAccessRoles on Query
      @refetchable(queryName: "AccessModelSettingsRefetchQuery") {
        ...GatewayRolesForm_awsAccessRoles
        awsAccessRoles {
          externalID
          primaryRoleARN
          samplePrimaryTrust
        }
      }
    `,
    queryRef,
  );

  const roleKeys = [
    "primaryRoleARN",
    "readOnlyRoleARN",
    "accountDiscoveryRoleARN",
    "remediationPolicyRoleARN",
  ];
  const userHasGatewayRoles = roleKeys.some(
    (roleKey) =>
      !!data.awsAccessRoles[roleKey as keyof typeof data.awsAccessRoles],
  );
  const [wasGatewayRolesToggled, setWasGatewayRolesToggled] = useState(false);
  const [isGatewayRolesToggled, setIsGatewayRolesToggled] =
    useState(userHasGatewayRoles);

  useEffect(() => {
    setIsGatewayRolesToggled(userHasGatewayRoles);
  }, [userHasGatewayRoles]);

  function disableGatewayRoles() {
    updateAwsAccessRoles({
      variables: {
        input: {
          primary: null,
          readOnly: null,
          accountDiscovery: null,
          remediationPolicy: null,
        },
      },
      onCompleted(_, errors) {
        if (errors) {
          Sentry.captureException(errors);
        } else {
          alertDispatch({
            message: "Gateway roles disabled successfully",
            severity: "success",
          });
        }
      },
      onError: (error) => {
        Sentry.captureException(error);
      },
      updater: setAwsAccessRolesUpdater,
    });
  }

  const { primaryRoleARN, externalID, samplePrimaryTrust } =
    data.awsAccessRoles;

  const executionRoleARN = getExecutionRole(samplePrimaryTrust);
  return (
    <>
      <V2Dialog
        aria-label="disable-gateway-roles-modal"
        isOpen={wasGatewayRolesToggled}
        onOpenChange={(isOpen) => {
          if (!isOpen) setWasGatewayRolesToggled(false);
        }}
        overlayClassName="z-[1300]"
        size="lg"
        isDismissable
      >
        <V2DialogHeader hideCloseButton>
          Disable Gateway Roles (Chain Assume Role)
        </V2DialogHeader>
        <V2DialogContent>
          <p className="mb-md text-body-large">
            Stacklet will use the default security context on each account
            rather than the gateway role.
          </p>
          <p className="text-body-large">
            Ensure that the roles on each account have a valid trust
            relationship to the Stacklet execution role to ensure that AssetDB,
            Policy Execution, and Account Discovery continues uninterrupted.
          </p>
        </V2DialogContent>
        <V2DialogFooter className="flex w-full justify-between">
          <V2Button
            onPress={() => setWasGatewayRolesToggled(false)}
            variant="tertiary"
          >
            Cancel
          </V2Button>
          <V2Button
            isDisabled={isInFlight}
            isPending={isInFlight}
            onPress={() => {
              setWasGatewayRolesToggled(false);
              disableGatewayRoles();
            }}
            pendingText="Disabling..."
            variant="primary"
          >
            Disable Gateway Roles
          </V2Button>
        </V2DialogFooter>
      </V2Dialog>
      <div className="flex w-[51.5625rem] flex-col gap-xl px-lg py-xs">
        <div className="flex flex-col gap-xl pl-1">
          <V2Switch
            isSelected={isGatewayRolesToggled}
            onChange={() => {
              if (userHasGatewayRoles) {
                setWasGatewayRolesToggled(true);
              } else {
                setIsGatewayRolesToggled((prev) => !prev);
              }
            }}
            position="trailing"
          >
            Enable Gateway Roles (Chain Assume Role)
          </V2Switch>
          <div className="flex flex-col gap-lg pl-4">
            <p className="text-body-small text-legacy-navy-L30">
              When <b>Gateway Roles (chain assume roles) are enabled</b>,
              Stacklet will first assume into the Gateway Roles you&apos;ve
              provided, and then assume into each accounts&apos; security
              context role. This allows you to disable Stacklet&apos;s policy
              execution access from a single role, by simply removing the trust
              relationship for Stacklet&apos;s execution role.
            </p>
            <ul className="list-disc space-y-lg px-xl text-body-small text-legacy-navy-L30">
              <li className="pl-lg">
                To enable chain assume role, create a “Primary Role” that can
                trust the Stacklet execution role by using this Trust
                Relationship:{" "}
                <V2CopyButton
                  aria-label="Copy Trust Relationship button in description list"
                  className="inline-flex"
                  copyLabel="Trust Relationship"
                  value={samplePrimaryTrust || ""}
                  variant="stacklet-orange"
                >
                  Copy Trust Relationship
                </V2CopyButton>
              </li>
              <li className="pl-lg">
                Next, ensure that the Primary Role has the ability to assume
                into the roles available in each account&apos;s security
                context.
              </li>
              <li className="pl-lg">
                Finally, add and save the Primary Role ARN.
              </li>
            </ul>
            <div className="flex flex-col gap-sm">
              <p className="text-label-medium text-legacy-navy-base">
                Stacklet execution role
              </p>
              <div className="w-fit rounded-1 border border-legacy-navy-L70 px-sm font-semibold text-legacy-orange-L20">
                {executionRoleARN}
              </div>
              <div className="relative flex items-center gap-xl">
                <V2CopyButton
                  className="text-label-small"
                  copyLabel="Role ARN"
                  value={primaryRoleARN || ""}
                >
                  Copy Role ARN
                </V2CopyButton>

                <V2Divider
                  className="h-3/4 self-center border-l-legacy-navy-D50"
                  orientation="vertical"
                  size="s"
                />
                <V2CopyButton
                  className="text-label-small"
                  copyLabel="External ID"
                  value={externalID || ""}
                >
                  Copy External ID
                </V2CopyButton>
              </div>
            </div>
            {isGatewayRolesToggled ? (
              <GatewayRolesForm
                queryRef={data}
                viewOnly={userHasGatewayRoles}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
